import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatchingService } from '../../services/matching.service';

@Component({
  selector: 'app-smart-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DecimalPipe],
  templateUrl: './smart-input.component.html',
  styleUrl: './smart-input.component.scss'
})
export class SmartInputComponent implements OnChanges {
    @Input() value: string | number = '';
    @Input() type = 'text';
    @Input() placeholder = '';
    @Input() error: string | null = null;
    @Input() touched = false;
    @Input() suggestion: any = null;

    // Matching Props
    @Input() matchingType: 'entity' | 'address' | 'account' | 'provider' | undefined;
    @Input() linkedId: string | undefined;
    @Input() locationBias: { state?: string; city?: string } | undefined;
    @Input() addressVerified: boolean = false;

    @Output() valueChange = new EventEmitter<string | number>();
    @Output() blur = new EventEmitter<void>();
    @Output() applySuggestionEvent = new EventEmitter<any>();
    @Output() linkMatch = new EventEmitter<{id: string, label: string}>();
    @Output() verifyAddress = new EventEmitter<void>();

    @ViewChild('inputEl') inputEl!: ElementRef;

    showDropdown = false;
    matches: any[] = [];
    private searchTimeout: any;
    private isEditing = false;

    constructor(
        private matchingService: MatchingService,
        private decimalPipe: DecimalPipe
    ) {}

    get displayValue(): string | number {
        // Don't reformat during editing - prevents cursor jump
        if (this.isEditing && this.inputEl?.nativeElement) {
            return this.inputEl.nativeElement.value;
        }
        if (this.type === 'number' && this.value !== '' && this.value !== null && this.value !== undefined) {
            const numVal = typeof this.value === 'string' ? parseFloat(this.value) : this.value;
            if (!isNaN(numVal)) {
                return this.decimalPipe.transform(numVal, '1.2-2') || '';
            }
        }
        return this.value;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['value'] && this.matchingType) {
            this.handleSearch(String(this.value));
        }
    }

    onInputChange(e: Event) {
        const val = (e.target as HTMLInputElement).value;
        if (this.type === 'number') {
            // Strip commas and parse
            const cleanVal = val.replace(/,/g, '');
            const numVal = parseFloat(cleanVal);
            this.valueChange.emit(isNaN(numVal) ? 0 : Math.round(numVal * 100) / 100);
        } else {
            this.valueChange.emit(val);
        }
    }

    get inputType(): string {
        // Use text for number type to allow comma formatting
        return this.type === 'number' ? 'text' : this.type;
    }

    onInputBlur() {
        this.isEditing = false;
        // Small delay to allow click on dropdown
        setTimeout(() => {
            this.showDropdown = false;
            this.blur.emit();
        }, 200);
    }

    onInputFocus() {
        this.isEditing = true;
        if (this.matches.length > 0 || this.linkedId) {
            this.showDropdown = true;
        }
    }

    onInputClick(e: MouseEvent) {
        // For address type that needs verification, trigger verification on click
        if (this.matchingType === 'address' && !this.linkedId && !this.addressVerified) {
            this.verifyAddress.emit();
        }
    }

    onDrop(e: DragEvent) {
        e.preventDefault();
        const droppedText = e.dataTransfer?.getData('text/plain');
        if (droppedText) {
            const finalVal = this.type === 'number' ? parseFloat(droppedText) : droppedText;
            if (this.type === 'number' && isNaN(finalVal as number)) return;

            this.valueChange.emit(finalVal);
            this.blur.emit();
        }
    }

    applySuggestion() {
        if (this.suggestion) {
            this.applySuggestionEvent.emit(this.suggestion.value);
        }
    }

    handleSearch(query: string) {
        if (this.searchTimeout) clearTimeout(this.searchTimeout);

        if (!query || query.length < 2) {
            this.matches = [];
            if (!this.linkedId) this.showDropdown = false;
            return;
        }

        this.searchTimeout = setTimeout(async () => {
            if (this.matchingType) {
                this.matches = await this.matchingService.findMatches(query, this.matchingType, this.locationBias);
            }
        }, 300);
    }

    toggleDropdown(e: MouseEvent) {
        e.preventDefault();
        this.showDropdown = !this.showDropdown;
    }

    handleBadgeClick(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();

        if (this.matchingType === 'address' && !this.linkedId && !this.addressVerified) {
            this.verifyAddress.emit();
        } else {
            this.toggleDropdown(e);
        }
    }

    selectMatch(match: any) {
        this.linkMatch.emit({ id: match.id, label: match.label });
        this.showDropdown = false;
    }

    createNew() {
        this.linkMatch.emit({ id: '', label: String(this.value) });
        this.showDropdown = false;
    }

    get topMatch() {
        return this.matches.length > 0 ? this.matches[0] : null;
    }

    get showBadge(): boolean {
        return !!this.matchingType && (this.value || '').toString().length > 2;
    }

    getBadgeText(): string {
        if (this.linkedId) return 'LINKED';
        if (this.matchingType === 'address' && this.addressVerified) return 'VERIFIED';
        if (this.matchingType === 'address' && !this.addressVerified) return 'VERIFY ADDRESS';
        if (this.topMatch && this.topMatch.score > 0.85) {
            return 'MATCH FOUND';
        }
        return 'NEW';
    }

    getBadgeClass(): string {
        if (this.linkedId) return 'linked';
        if (this.matchingType === 'address' && this.addressVerified) return 'verified';
        if (this.matchingType === 'address' && !this.addressVerified) return 'warning';
        if (this.topMatch && this.topMatch.score > 0.85) return 'warning';
        return 'new';
    }

    getBadgeIcon(): string {
        if (this.linkedId) return 'link';
        if (this.matchingType === 'address' && this.addressVerified) return 'check';
        if (this.matchingType === 'address' && !this.addressVerified) return 'error';
        if (this.topMatch && this.topMatch.score > 0.85) return 'error';
        return 'check';
    }

    getInputClass(): string {
        const classes: string[] = [];
        if (this.linkedId) classes.push('linked');
        if (this.error && this.touched) classes.push('has-error');
        if (this.showBadge) classes.push('has-badge');
        if (this.matchingType === 'address' && !this.addressVerified && !this.linkedId) {
            classes.push('clickable');
        }
        return classes.join(' ');
    }

    getMatchIconClass(match: any): string {
        if (this.linkedId === match.id) return 'linked';
        return 'default';
    }

    getMatchIcon(match: any): string {
        if (this.linkedId === match.id) return 'link';
        if (match.score > 0.9) return 'check';
        return 'search';
    }
}
